import { Component, ReactNode } from "react";
import {
  hasExceededModuleImportRecoveryAttempts,
  isModuleImportFailure,
  recoverFromModuleImportFailure,
  resetModuleImportRecoveryState,
} from "@/lib/moduleLoadRecovery";

type Props = { children: ReactNode };
type State = {
  hasError: boolean;
  isModuleError: boolean;
  recoveryExhausted: boolean;
};

export class ModuleLoadBoundary extends Component<Props, State> {
  state: State = { hasError: false, isModuleError: false, recoveryExhausted: false };

  static getDerivedStateFromError(error: unknown): State {
    const isModule = isModuleImportFailure(error);
    return {
      hasError: true,
      isModuleError: isModule,
      recoveryExhausted: isModule && hasExceededModuleImportRecoveryAttempts(),
    };
  }

  override componentDidCatch(error: unknown) {
    if (isModuleImportFailure(error)) {
      void recoverFromModuleImportFailure().then((ok) => {
        if (!ok) this.setState({ recoveryExhausted: true });
      });
      return;
    }
    console.error("[ModuleLoadBoundary] non-module error:", error);
  }

  private handleRetry = () => {
    resetModuleImportRecoveryState();
    window.location.reload();
  };

  override render() {
    if (!this.state.hasError) return this.props.children;

    // Module error still attempting recovery — show spinner
    if (this.state.isModuleError && !this.state.recoveryExhausted) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
          <div className="flex flex-col items-center gap-3 text-center text-muted-foreground">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-sm font-medium">Restabelecendo módulos...</p>
          </div>
        </div>
      );
    }

    // Either module error exhausted, or a generic rendering error.
    // Show a user-friendly retry screen.
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="flex max-w-sm flex-col items-center gap-4 px-6 text-center">
          <p className="text-sm text-muted-foreground">
            {this.state.isModuleError
              ? "Não foi possível carregar os arquivos desta tela. Verifique sua conexão e tente novamente."
              : "Ocorreu um erro inesperado ao carregar esta tela."}
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-opacity hover:opacity-90"
          >
            Recarregar página
          </button>
        </div>
      </div>
    );
  }
}
